import type { CopyAction, DescriptorAction, OpenAction, PathActions, ReadAction, WriteAction, ZipAction, HashFileInfo } from './Wrapper';

import { BlobWriter, Uint8ArrayReader, ZipWriter } from '@zip.js/zip.js';
import { createInstance } from 'localforage';
import { hash as SparkHash, ArrayBuffer as SparkBuffer } from 'spark-md5';

import { _sendReply, enableSelfTalk } from '../Communication';
import { registerDebugAppAction } from '../DebugImplementation/AppAction';
import { registerDebugAuthService } from '../DebugImplementation/AuthService';
import { registerDebugEventManager } from '../DebugImplementation/EventManager';
import { registerDebugInformation } from '../DebugImplementation/Information';
import { registerDebugSessionService } from '../DebugImplementation/SessionService';
import { registerDebugShare } from '../DebugImplementation/Share';
import { registerDebugSplashScreen } from '../DebugImplementation/SplashScreen';
import { registerHandler } from '../MessageManager';

import { writeFile, readBinaryFile, findAllFiles } from './Helper';

import { isReactNativeApp } from '@egr/xbox/app-api/AppDetection';
import { getArrayBuffer } from '@egr/xbox/utils/Blob';
import { assert } from '@egr/xbox/utils/Debug';
import { TaskCanceledError } from '@egr/xbox/utils/Promise';
import { getEnvVar } from '@egr/xbox/utils/ReactScriptHelper';

import { toBase64, fromBase64 } from '@easterngraphics/wcf/modules/utils/arraybuffer';

interface FileWrapper {
    read(size: number): Promise<string>;
    write(data: string): Promise<void>;
}

const db: LocalForage = ((): LocalForage => {
    return createInstance({
        name: getEnvVar('ID', 'missing-env-id'),
        version: 1.0,
        storeName: 'fsapi',
        description: 'Debug implementation of the app file system api',
    });
})();
const fdIndex: Record<string, FileWrapper> = {};
let fdCount: number = 0;

function storageKeys(): Promise<Array<string>> {
    return db.keys();
}

async function storageGet(key: string): Promise<string | ArrayBuffer | undefined | null> {
    return await db.getItem(key);
}

async function storageSet(key: string, value: string | ArrayBuffer | null | undefined): Promise<void> {
    await db.setItem(key, value === undefined ? null : value);
}

async function storageDel(key: string): Promise<void> {
    return db.removeItem(key);
}

async function storageAdd(key: string, value: string | ArrayBuffer): Promise<void> {
    const content: string | ArrayBuffer | undefined | null = await storageGet(key);

    if (content == null) {
        return storageSet(key, value);
    }

    // Note: since this is just a debug function, we use type casts instead of type guards

    if (value instanceof ArrayBuffer) {
        const newContent: Uint8Array = new Uint8Array((content as ArrayBuffer).byteLength + value.byteLength);
        newContent.set(new Uint8Array(content as ArrayBuffer));
        newContent.set(new Uint8Array(value), (content as ArrayBuffer).byteLength);

        return storageSet(key, getArrayBuffer(newContent));
    }

    if (content instanceof ArrayBuffer) {
        throw new Error('Type mismatch');
    }

    return storageSet(key, content + value);
}

function unifyPath(path: string, onlyFolderAllowed: boolean = false): string {
    const regexFolder: RegExp = /^([a-z0-9\.\-_]+)$/m;
    const regexFilename: RegExp = /^([a-z0-9\.\-_]+)\.([a-z0-9]+)$/m;

    if (!path.startsWith('/')) {
        throw new Error('Unsupported path');
    }

    if (onlyFolderAllowed === true && !path.endsWith('/')) {
        throw new Error('Only folders allowed');
    }

    const parts: Array<string> = path.split('/').slice(1); // path starts with '/' -> first index is always empty

    const folders: Array<string> = parts.slice(0, -1);
    let filename: string | null = null;
    if (!path.endsWith('/')) {
        filename = parts[parts.length - 1];
    }

    for (const folder of folders) {
        if (folder.startsWith('.') || !regexFolder.test(folder)) {
            throw new Error('Unsupported path');
        }
    }

    if (filename != null && (filename.startsWith('.') || !regexFilename.test(filename))) {
        throw new Error('Unsupported filename');
    }

    return path.toLowerCase();
}

function getFileName(path: string): string {
    path = unifyPath(path);

    return path.slice(path.lastIndexOf('/'), path.length);
}

async function getStorageKeysStartingWith(path: string): Promise<Array<string>> {
    const keys: Array<string> = await storageKeys();
    return keys.filter((value: string): boolean => {
        return value.startsWith(path) && value !== path;
    });
}

interface Operation<ActionType, ArgumentsType> {
    transaction_id: number;

    action: ActionType;
    arguments: ArgumentsType;
}

type Operations =
    | Operation<'close', DescriptorAction>
    | Operation<'open', OpenAction>
    | Operation<'flush', DescriptorAction>
    | Operation<'write', WriteAction>
    | Operation<'read', ReadAction>
    | Operation<'remove', PathActions>
    | Operation<'size', PathActions>
    | Operation<'hash', { type: string; paths: Array<string> }>
    | Operation<'list', PathActions>
    | Operation<'zip', ZipAction>
    | Operation<'copy', CopyAction>
    | Operation<'syncDirectories', Omit<CopyAction, 'override'>>
    | Operation<'hasSyncLock', { directory: string }>
    | Operation<'createSyncLock', { directory: string }>
    | Operation<'removeSyncLock', { directory: string }>;

async function backend(event: MessageEvent): Promise<void> {
    const data: Operations = event.data;

    let returnValue: null | string | Array<string> | void | number | Array<HashFileInfo> | boolean = null;

    try {
        switch (data.action) {
            case 'open':
                if (data.arguments.path.endsWith('/') && data.arguments.mode === 'r') {
                    throw Error('Illegal Operation on Directory: read');
                }

                returnValue = await open(data.arguments.path, data.arguments.mode, data.arguments.converter);
                break;
            case 'close':
                returnValue = close(data.arguments.descriptor);
                break;
            case 'flush':
                returnValue = flush(data.arguments.descriptor);
                break;
            case 'write':
                returnValue = await write(data.arguments.descriptor, data.arguments.data);
                break;
            case 'read':
                returnValue = await read(data.arguments.descriptor, data.arguments.size);
                break;
            case 'remove':
                returnValue = await remove(data.arguments.path);
                break;
            case 'size':
                returnValue = await size(data.arguments.path);
                break;
            case 'list':
                returnValue = await list(data.arguments.path);
                break;
            case 'copy':
                returnValue = await copy(data.arguments.source, data.arguments.destination, data.arguments.override);
                break;
            case 'zip':
                returnValue = await zip(data.arguments.target, data.arguments.src, data.arguments._debugFsOnly_ignoreAndLogErrors);
                break;
            case 'hash':
                returnValue = await hash(data.arguments.paths, data.arguments.type);
                break;
            case 'syncDirectories':
                returnValue = await syncDirectories(data.arguments.source, data.arguments.destination);
                break;
            case 'hasSyncLock':
                returnValue = (await storageGet(data.arguments.directory + '.sync_lock')) != null;
                break;
            case 'createSyncLock':
                returnValue = await storageSet(data.arguments.directory + '.sync_lock', '1');
                break;
            case 'removeSyncLock':
                returnValue = await storageDel(data.arguments.directory + '.sync_lock');
                break;
            default:
                throw new Error('Unsupported action');
        }
        _sendReply(data.transaction_id, false, returnValue);
    } catch (error) {
        _sendReply(data.transaction_id, true, error.stack);
    }
}

function close(descriptor: string): void {
    delete fdIndex[descriptor];
}

async function copy(src: string, dst: string, override: boolean): Promise<void> {
    src = unifyPath(src);
    dst = unifyPath(dst);

    if (src.endsWith('/') && dst.endsWith('/')) {
        const entries: Array<string> = await list(src);
        await Promise.all<void>(
            entries.map((value: string): Promise<void> => {
                return copy(src + value, dst + value, override);
            })
        );
        return;
    }

    if (src.endsWith('/') && !dst.endsWith('/')) {
        throw new Error('The `dst` has to end with `/` if the `src` ends with `/`');
    }

    if (dst.endsWith('/') && !src.endsWith('/')) {
        dst = dst + getFileName(src);
    }

    const content: string | ArrayBuffer | undefined | null = await storageGet(src);
    if (content != null) {
        if (override || (await storageGet(dst)) == null) {
            return storageSet(dst, await storageGet(src));
        }

        throw new Error('File already exists: ' + dst);
    }

    throw new Error('Source not found: ' + src);
}

async function syncDirectories(src: string, dst: string): Promise<void> {
    src = unifyPath(src);
    dst = unifyPath(dst);

    if (!src.endsWith('/') || !dst.endsWith('/')) {
        throw new Error('Source and destination must be directories (end with "/")');
    }

    const lockFile = dst + '.sync_lock';
    await storageSet(lockFile, '1');
    await copy(src, dst, true);
    await storageDel(lockFile); // Important: do not use try..finally here
}

function flush(descriptor: string): void {
    if (fdIndex[descriptor] == null) {
        throw new Error('Invalid file descriptor');
    }
}

async function list(path: string): Promise<Array<string>> {
    path = unifyPath(path, true);

    const l: number = path.length;
    const keys: Array<string> = await getStorageKeysStartingWith(path);
    const entries: Array<string> = keys
        .map((value: string): string => {
            const relPath: string = value.slice(l, value.length);
            const dirPos: number = relPath.indexOf('/');
            if (dirPos !== -1) {
                return relPath.slice(0, dirPos + 1);
            }
            return relPath;
        })
        .filter((value: string, pos: number, filteredArray: Array<string>): boolean => {
            return filteredArray.indexOf(value) === pos;
        });

    if (entries.length === 0 && path !== '/') {
        // FixMe: add support for empty directories
        throw new Error('No such directory');
    }

    assert(entries.indexOf('') === -1, 'Directory list should not contain an empty string!');

    return entries;
}

async function open(path: string, mode: 'r' | 'w', converter: 'base64' | ''): Promise<string> {
    path = unifyPath(path);

    if (['w', 'r'].indexOf(mode) === -1) {
        throw new Error('Unsupported mode: ' + mode);
    }

    const content: string | ArrayBuffer | undefined | null = await storageGet(path);
    if (content == null) {
        if (mode !== 'r') {
            await storageSet(path, null);
        } else {
            throw new Error('No such file: ' + path);
        }
    } else if (mode === 'w') {
        await storageSet(path, null);
    }

    const id: string = (fdCount++).toString();
    let position: number = 0;

    fdIndex[id] = ((): FileWrapper => {
        return {
            read: async (length: number): Promise<string> => {
                const rawData: string | ArrayBuffer | undefined | null = await storageGet(path);
                if (rawData == null) {
                    return '';
                }

                try {
                    if (converter === 'base64') {
                        if (!(rawData instanceof ArrayBuffer)) {
                            const encoder: TextEncoder = new TextEncoder();
                            const encodedData: Uint8Array = encoder.encode(rawData);
                            return toBase64(getArrayBuffer(encodedData));
                        }

                        return toBase64(rawData.slice(position, position + length));
                    }

                    if (converter === '') {
                        if (rawData instanceof ArrayBuffer) {
                            const decoder: TextDecoder = new TextDecoder();
                            const decodedData: string = decoder.decode(rawData);
                            return decodedData.substr(position, length);
                        }
                        return rawData.substr(position, length);
                    }
                } finally {
                    position += length;
                }

                throw new Error('Unsupported converter: ' + (converter as string));
            },
            write: async (data: string): Promise<void> => {
                if (converter === 'base64') {
                    await storageAdd(path, fromBase64(data));
                } else if (converter === '') {
                    await storageAdd(path, data);
                } else {
                    throw new Error('Unsupported converter: ' + (converter as string));
                }
            },
        };
    })();

    return id;
}

function read(descriptor: string, length: number): Promise<string> {
    return fdIndex[descriptor].read(length);
}

async function remove(path: string): Promise<void> {
    path = unifyPath(path);

    if (path === '/') {
        throw new Error('Forbidden');
    }

    if (path.endsWith('/')) {
        const keys: Array<string> = await getStorageKeysStartingWith(path);

        if (keys.length === 0) {
            throw new Error('no such directory');
        }

        await Promise.all(keys.map(storageDel));
    } else {
        return storageDel(path);
    }
}

async function size(path: string): Promise<number> {
    path = unifyPath(path);

    if (path.endsWith('/')) {
        const entries: Array<string> = await list(path);
        const sizes: Array<number> = await Promise.all<number>(
            entries.map((value: string): Promise<number> => {
                return size(path + value);
            })
        );
        return sizes.reduce((a: number, b: number): number => {
            return a + b;
        });
    }

    const content: string | ArrayBuffer | undefined | null = await storageGet(path);
    if (content == null) {
        return 0;
    }

    if (content instanceof ArrayBuffer) {
        return content.byteLength;
    }

    return content.length;
}

// Note: the size and hash do not necessary match the values generated by the real app api
// This code doesn't know if the data is text or binary (stored as base64 encoded text).
// Therefore the generated values can be incorrect.
async function hash(paths: Array<string>, hashType: string): Promise<Array<HashFileInfo>> {
    if (hashType !== 'md5') {
        throw new Error('Unsupported hash type: ' + hashType);
    }

    const reply: Array<HashFileInfo> = [];

    for (const path of paths) {
        const data: string | ArrayBuffer = (await storageGet(path)) ?? '';
        reply.push({
            size: data instanceof ArrayBuffer ? data.byteLength : data.length,
            hash: data instanceof ArrayBuffer ? SparkBuffer.hash(data) : SparkHash(data),
        });
    }

    return reply;
}

function write(descriptor: string, data: string): Promise<void> {
    return fdIndex[descriptor].write(data);
}

async function zip(target: string, src: string, ignoreAndLogErrors?: boolean): Promise<void> {
    const l: number = src.length;
    const zipWriter = new ZipWriter<Blob>(new BlobWriter('application/zip'));

    const errors: Array<string> = [];
    const logger = (path: string, error: string | Error) => {
        if (error instanceof Error) {
            errors.push(`==> Path: ${path}\r\n${error.name}: ${error.message}\r\n${error.stack ?? ''}\r\n`);
        } else {
            errors.push(`==> Path: ${path}\r\n${error}\r\n`);
        }
    };

    for await (const file of findAllFiles(src, undefined, logger)) {
        try {
            const content: ArrayBuffer = await readBinaryFile(file);
            await zipWriter.add(file.substr(l), new Uint8ArrayReader(new Uint8Array(content)));
        } catch (error) {
            if (!ignoreAndLogErrors) {
                throw error;
            }

            logger(file, error);
        }
    }

    if (errors.length) {
        const encodedErrors = new TextEncoder().encode(errors.join('\r\n'));
        await zipWriter.add('___error___.log', new Uint8ArrayReader(encodedErrors));
    }

    const blob: Blob = await zipWriter.close();
    const data: ArrayBuffer = await blob.arrayBuffer();
    await writeFile(target, data);
}

const cancelMap = new Map<number, string>();
function getCancelCheck(transactionId: number): VoidFunction {
    return (): void => {
        const message: string | undefined = cancelMap.get(transactionId);
        if (message != null) {
            cancelMap.delete(transactionId);
            throw new TaskCanceledError(message);
        }
    };
}

export function registerFakeFsApi(): void {
    if (isReactNativeApp) {
        return;
    }

    enableSelfTalk();
    registerDebugSplashScreen();

    registerHandler({
        type: 'fs',
        callback: backend,
    });

    registerDebugInformation();
    registerDebugAuthService();
    registerDebugAppAction(cancelMap);
    registerDebugEventManager();
    registerDebugShare(remove, getCancelCheck);
    registerDebugSessionService();
}
